// Parts catalog row drilldown. Read-only view of the catalog row + inline
// editable metadata (PATCH /api/parts-catalog/:id) + inventory rollup per
// location + revision breakdown from receipt_items (the hub feed).
//
// SKU is immutable post-create (backend rejects it in the patch payload), so it
// is rendered as text under the name in the page header rather than as a field.
// Renaming it is a separate, admin-only door beside that header, alongside the
// other two things only an admin does to a whole item: merging one in, and
// retiring it (is_active, which is what takes it out of every picker).
//
// ── THIS IS WHERE IDENTITY IS EDITED ────────────────────────────────────────
// The name and the five facts a name is generated FROM (kind, drone
// generation, revision, variant, PCBA vendor, prototype) live at the top of the
// metadata card, above the classification axes. They are one group because
// changing any of them changes what every other screen calls this item: the
// server recomputes base_name / variant_detail / display_name from them, and
// the name field only overrides the first half.
//
// The drone generation is the generation of the DRONE this part is FOR. The
// part's own iteration is the revision. They have been read as each other
// before, which is why the field is labeled "Drone generation" and carries a
// helper line saying so.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Field as TextField } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import { Tooltip } from '../components/ui/Tooltip';
import { Link, useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import PageLayout from '../components/ui/PageLayout';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import useUserStore from '../stores/useUserStore';
import RenameSkuModal from './RenameSkuModal';
import MergeItemModal from './MergeItemModal';
import ChangeKindModal from './ChangeKindModal';
import ItemName, { itemLabel } from '../components/ItemName';
import ItemKindForm from '../Admin/ItemKindForm';
import { VendorForm, type VendorFullRow } from '../Admin/VendorsAdmin';
import type { VendorTypeOption } from '../Procurement/vendorTypeLabels';
import useItemKindsStore, {
  structuredKinds,
  shortKinds,
  kindByKey,
  type ItemKind,
} from '../stores/useItemKindsStore';
import HelpDrawer from '../components/HelpDrawer';
import { formatEventDate } from '../utils/date-utils';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { formatTimestamp } from '../utils/date-utils';
import InventoryAdjustAction from './InventoryAdjustAction';
import ClassificationGuideButton from './ClassificationGuide';
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_META,
  MaterialTypeBadge,
  COMMODITY_CLASSES,
  COMMODITY_CLASS_META,
  CommodityClassBadge,
} from './classificationDisplay';
import PartFamilyField from './PartFamilyField';
import type {
  PartsCatalogRow,
  PartFamilyRow,
  InventoryRollupRow,
  MaterialType,
  CommodityClass,
} from '../Receiving/types';

/** One item off the hub feed; `barcode` is its box's sticker. */
interface ReceiptItemLite {
  id: string;
  barcode: string;
  revision: string | null;
  manufacture_date: string | null;
  lot_number: string | null;
  vendor: string | null;
  quantity_received: number;
  quantity_remaining: number;
  unit_cost_cents: number | null;
  received_at: string;
  sku: string | null;
  tracked_per_instance: boolean;
}

/** The server's own limit on parts_catalog.name. */
const NAME_MAX = 60;

/** The Kind picker's last option. A sentinel no item_kinds key can hold: keys
 *  are 2 to 4 characters, uppercase, letter first. */
const NEW_KIND = '__new__';

/** The PCBA vendor picker's last option, for the same reason the Kind picker
 *  has one: a value that does not exist yet is created in place. A uuid can
 *  never collide with it. */
const NEW_VENDOR = '__new_vendor__';

/** A kind's human label, retired kinds included: this answers "what is this
 *  item", and that does not stop being true when the kind stops being offered. */
const kindLabel = (kinds: ItemKind[], key: string): string | null =>
  kindByKey(kinds, key)?.label || null;

const formatCurrency = (n: string | number | null) =>
  n === null || n === undefined
    ? '—'
    : `$${(typeof n === 'string' ? parseFloat(n) : n / 100).toFixed(2)}`;

/** The lean vendor projection GET /procurement/vendors returns. Ids are uuids
 *  (migration 009), which is why pcba_vendor_id is a string everywhere. */
interface PcbaVendor {
  id: string;
  code: string | null;
  name: string;
  /** Retired. Absent on a vendor this page created, which is always active. */
  is_disabled?: boolean;
}

/** A vendor's name for a place that only reads it. Retired assemblers are named
 *  and marked, never hidden: which shop built a board is a fact about the board,
 *  and it does not stop being true when we stop buying from them. */
const vendorLabel = (v: PcbaVendor): string => (v.is_disabled ? `${v.name} (retired)` : v.name);

/** The same fact in a dropdown, where the two-letter code also has to fit. One
 *  bracket, not two: "Green Circuits (GC, retired)". */
const vendorOptionLabel = (v: PcbaVendor): string => {
  const bits = [v.code, v.is_disabled ? 'retired' : null].filter(Boolean);
  return bits.length ? `${v.name} (${bits.join(', ')})` : v.name;
};

interface SkuHistoryRow {
  sku: string;
  retired_at: string;
  restored_at: string | null;
  reason: string | null;
  firstname: string | null;
  lastname: string | null;
}

/** One revision's boxes, rolled up for the breakdown table. */
interface RevisionRow {
  rev: string;
  boxes: ReceiptItemLite[];
  remaining: number;
  received: number;
}

// ---------------------------------------------------------------------------
// The two read-only sub-tables on this page, as shared-DataTable columns.
//
// Module scope per the DataTable contract: every cell is pure over its row and
// nothing closes over page state, so neither table needs `meta`. No `sortable`
// and no accessorFn either — the rollup arrives in the server's order, and the
// revision breakdown is already ordered by how many boxes each revision has,
// which is the question the section is asked.
//
// NEITHER TABLE CARRIES A FINANCIAL COLUMN, and that is deliberate. Cost on
// this page is a single field in the metadata card, gated there by
// can_financial; `unit_cost_cents` rides the box rows unrendered. A cost column
// here would be new exposure rather than a migration, so there is no
// columnVisibility on either vessel and nothing to leak if one is added later
// without one.
// ---------------------------------------------------------------------------

const inventoryRowId = (r: InventoryRollupRow) => `${r.part_catalog_id}-${r.location_id}`;
const revisionRowId = (r: RevisionRow) => r.rev;

const INVENTORY_COLUMNS: ColumnDef<InventoryRollupRow, any>[] = [
  {
    id: 'location',
    header: 'Location',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.location_name || '—',
  },
  {
    id: 'quantity',
    header: 'Quantity',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.quantity,
  },
  {
    id: 'updated',
    header: 'Updated',
    meta: { numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => formatTimestamp(row.original.updated_at, 'MMM D, YYYY h:mm A'),
  },
];

const REVISION_COLUMNS: ColumnDef<RevisionRow, any>[] = [
  {
    id: 'revision',
    header: 'Revision',
    meta: { cellClass: 'text-on-surface font-mono' },
    cell: ({ row }) => row.original.rev,
  },
  {
    // Every box of that revision, each one a link to itself: this column is how
    // somebody gets from "there are three of rev C" to the box in their hands.
    id: 'boxes',
    header: 'Boxes',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => (
      <>
        {row.original.boxes.map((b) => (
          <Link
            key={b.id}
            to={`/receiving/item/${b.id}`}
            className="text-xs font-mono text-primary hover:underline mr-2"
          >
            {b.barcode}
          </Link>
        ))}
      </>
    ),
  },
  {
    id: 'remaining',
    header: 'Remaining',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.remaining,
  },
  {
    id: 'received',
    header: 'Received',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.received,
  },
];

const PartMPNDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const user = useUserStore((s) => s.user);
  const canEdit = user.isadmin || user.can_admin || user.can_procurement;
  // Stock adjusting is not a catalog edit: it follows POST /api/inventory/adjust
  // (operations | admin), the same gate InventoryAdjustAction applies inside.
  const canAdjust = user.isadmin || user.can_admin || user.can_operations;
  const isAdmin = user.isadmin || user.can_admin;
  // Strict: cost/price visibility is opt-in even for admins (see require-financial).
  const canSeeFinancials = !!user.can_financial;

  const [row, setRow] = useState<PartsCatalogRow | null>(null);
  const [families, setFamilies] = useState<PartFamilyRow[]>([]);
  const [inventory, setInventory] = useState<InventoryRollupRow[]>([]);
  const [recentBoxes, setRecentBoxes] = useState<ReceiptItemLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<PartsCatalogRow>>({});
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [merging, setMerging] = useState(false);
  const [changingKind, setChangingKind] = useState(false);
  /** Retire/Restore is in flight. One flag, because the two are one control. */
  const [retiring, setRetiring] = useState(false);
  // Former names (migration 030). Gated operations|admin server-side because
  // the rows carry a free-text reason, so a failure here is silent by design —
  // the panel simply does not render rather than erroring at people who are
  // not meant to see it.
  const [skuHistory, setSkuHistory] = useState<SkuHistoryRow[]>([]);
  // PCBA vendors for the board's assembler field. Same list, same endpoint the
  // mint form and the PO form read. A failure is tolerated: the select renders
  // with the current answer and nothing else, rather than blocking the page.
  const [pcbaVendors, setPcbaVendors] = useState<PcbaVendor[]>([]);
  // Rides on the same response as the vendors above (procurement-router.js ships
  // the active taxonomy as `types` on GET /vendors, which is the endpoint the
  // vendor FORM is meant to read; /vendor-types is the admin projection and
  // includes retired ones).
  const [vendorTypes, setVendorTypes] = useState<VendorTypeOption[]>([]);
  const [addingKind, setAddingKind] = useState(false);
  const [addingVendor, setAddingVendor] = useState(false);

  // The kind taxonomy, cached per session by the store. Subscribe to the raw
  // array and derive: the store's own structuredKinds() returns a fresh array
  // per call, so calling it inside a selector re-renders forever.
  const kinds = useItemKindsStore((s) => s.kinds);
  const loadKinds = useItemKindsStore((s) => s.load);

  const partCatalogId = parseInt(id, 10);

  const refreshFamilies = useCallback(async () => {
    try {
      setFamilies(((await api.get('/part-families')) as PartFamilyRow[]) || []);
    } catch {
      // The select keeps the list it had.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // GET /parts-catalog/:id, added with migration 068. This used to fetch the
      // whole catalog with page_size=500 and pick the row out client-side, which
      // was defensible only while the catalog was tens of rows and never while
      // an item has a page worth reading on its own.
      //
      // 404 is a real answer here (a merged-away item, a hand-typed id), and it
      // lands in the catch below as the toast the page already had.
      const [found, fams, inv, boxes, history] = await Promise.all([
        api.get(`/parts-catalog/${partCatalogId}`) as Promise<PartsCatalogRow>,
        api.get('/part-families') as Promise<PartFamilyRow[]>,
        api.get(`/inventory?part_catalog_id=${partCatalogId}`) as Promise<InventoryRollupRow[]>,
        api.get(`/receipt-boxes?days=90&limit=100`) as Promise<{ rows: ReceiptItemLite[] }>,
        // Tolerated failure: this endpoint is capability-gated and the page is
        // not. An empty list is the right answer for someone who may not read
        // rename reasons.
        (api.get(`/parts-catalog/${partCatalogId}/sku-history`) as Promise<SkuHistoryRow[]>)
          .catch(() => [] as SkuHistoryRow[]),
      ]);
      setRow(found || null);
      setDraft({});
      setFamilies(fams || []);
      setInventory(inv || []);
      // Filter boxes to this SKU only.
      setRecentBoxes((boxes.rows || []).filter((b) => b.sku === found?.sku));
      setSkuHistory(history || []);
    } catch (err: any) {
      // A 404 is a real answer on this route, not a failure: the id may name an
      // item that has since been merged away. Its body carries a code and no
      // sentence, so one is supplied rather than showing "[object Object]".
      const code = err?.data?.error?.code;
      toast.error(
        code === 'not_found'
          ? 'That item is not in the catalog any more.'
          : err?.message || 'Failed to load this item'
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partCatalogId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    loadKinds(api);
    let cancelled = false;
    (async () => {
      try {
        // includeDisabled, because this list has TWO jobs and only one of them
        // is "what may I pick". The other is naming the assembler already on a
        // board, and a contract manufacturer we stopped using is exactly the
        // case where the active-only list rendered "Unknown vendor" over a shop
        // whose name we have. Retired rows are filtered back out of the picker
        // below; they are here to be read, not chosen.
        // Any type, not only board houses: a purchased board's maker is its
        // assembler whatever the vendor master files it as.
        const data = (await api.get('/procurement/vendors?includeDisabled=1')) as {
          vendors: PcbaVendor[];
          types?: VendorTypeOption[];
        };
        if (cancelled) return;
        setPcbaVendors(data.vendors || []);
        setVendorTypes(data.types || []);
      } catch {
        // Tolerated. The field still shows what is stored.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The revision breakdown's rows: boxes grouped by revision, ordered by how
  // many boxes each revision has. ABOVE the loading early-return because a hook
  // cannot sit after one, and memoized rather than derived inline so typing in
  // the metadata editor does not hand the table a fresh row array per keystroke.
  const revisionRows = useMemo<RevisionRow[]>(() => {
    const byRevision = recentBoxes.reduce<Record<string, ReceiptItemLite[]>>((acc, b) => {
      const k = b.revision || '(no rev)';
      (acc[k] ||= []).push(b);
      return acc;
    }, {});
    return Object.entries(byRevision)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([rev, list]) => ({
        rev,
        boxes: list,
        remaining: list.reduce((s, b) => s + b.quantity_remaining, 0),
        received: list.reduce((s, b) => s + b.quantity_received, 0),
      }));
  }, [recentBoxes]);

  // Board kinds first, then the short-code stems, each in its own group. Two
  // flat lists would put "Grommet" between two board types and lose the one
  // distinction the taxonomy actually makes.
  const boardKinds = structuredKinds(kinds);
  const stemKinds = shortKinds(kinds);
  // A retired kind stays on the item wearing it. Without this the select would
  // show blank over a row that genuinely has a kind.
  const kindKnown = (key: string) =>
    boardKinds.some((k) => k.key === key) || stemKinds.some((k) => k.key === key);

  if (loading) return <LogoSpinner label="Loading this item" />;

  // The item did not load. Previously this state was an eternal spinner, which
  // was survivable while the page fetched the whole catalog and could only fail
  // wholesale; on a single-row route a 404 is an ordinary outcome and needs a
  // way out.
  if (!row)
    return (
      <PageLayout kind="record" className="space-y-3 pt-4">
        <BackLink to="/parts-catalog" label="Catalog" />
        <EmptyState title="That item is not in the catalog.">
          It may have been merged into another one, or deleted before anything had used it.
        </EmptyState>
      </PageLayout>
    );

  const totalOnHand = inventory.reduce((s, i) => s + (i.quantity || 0), 0);

  const handleSave = async () => {
    if (!canEdit) return;
    const body: Partial<PartsCatalogRow> = { ...draft };
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    try {
      const updated = (await api.patch(`/parts-catalog/${row.id}`, { body })) as PartsCatalogRow;
      setRow(updated);
      setDraft({});
      setEditing(false);
      toast.success('Saved');
    } catch (err: any) {
      const code = err?.data?.error?.code;
      if (code === 'tracked_per_instance_admin_only') {
        toast.error('Only admins can flip per-instance tracking');
      } else {
        toast.error(err?.message || 'Save failed');
      }
    }
  };

  /**
   * Retire, and un-retire.
   *
   * A prototype that did not work out, or a part we have stopped buying, is
   * not a mistake to delete: it has stock, boxes and orders behind it. It is
   * an item nobody should be able to order MORE of, which is exactly one
   * column. `GET /parts-catalog` hides inactive rows unless asked, so every
   * picker and every search loses it the moment this flips.
   *
   * Same shape as handleSave: the PATCH response IS the row, so nothing else
   * is fetched. Only retiring asks first; restoring puts a thing back, and a
   * dialog in front of that is a dialog in front of undo.
   */
  const setActive = async (next: boolean) => {
    if (!canEdit || retiring) return;
    if (!next) {
      const ok = await confirm({
        title: 'Retire this item?',
        message:
          'It leaves every picker and search, so nobody picks it for new work by hand. '
          + 'Stock, history and open orders stay exactly as they are, and you can restore '
          + 'it from this page.',
        confirmLabel: 'Retire',
      });
      if (!ok) return;
    }
    setRetiring(true);
    try {
      const updated = (await api.patch(`/parts-catalog/${row.id}`, {
        body: { is_active: next },
      })) as PartsCatalogRow;
      setRow(updated);
      toast.success(next ? 'Restored' : 'Retired');
    } catch (err: any) {
      toast.error(err?.message || (next ? 'Restore failed' : 'Retire failed'));
    } finally {
      setRetiring(false);
    }
  };

  const get = <K extends keyof PartsCatalogRow>(key: K): PartsCatalogRow[K] =>
    (draft[key] !== undefined ? draft[key] : row[key]) as PartsCatalogRow[K];

  return (
    <PageLayout
      kind="record"
      className="space-y-5 pt-4"
      header={
        <>
      {/* Quiet, and first. A retired item's page still shows every fact it had,
          so without this the only tell is a picker somewhere else not offering
          it. One step up from the page, no color: this is a state, not an
          error, and nothing here needs fixing. */}
      <Alert show={!row.is_active} tone="info" live={false}>
        Retired. It is hidden from pickers and search; restore it to order against it
        again.
      </Alert>
      <div>
        <BackLink to="/parts-catalog" label="Catalog" className="mb-2" />
        <div className="flex flex-wrap items-baseline gap-3 mt-1">
          {/* The name is the title and the SKU sits under it, mono and two
              steps down. This is one of the two places the code is shown at
              all: a detail page and a printed label. */}
          <h1 className="min-w-0">
            <ItemName item={row} size="lg" showSku />
          </h1>
          <HelpDrawer title="About this item">
            <p>
              Everything the company knows about one part: what it is, how it is
              classified, how much is in stock and where, and the boxes it arrived in.
            </p>
            <p className="text-on-surface font-medium">Lead time</p>
            <p>
              How many days from placing an order to the goods landing. It is blank until
              somebody asks the vendor, and blank is not the same as zero. It matters
              because it answers the only question that really counts when stock runs low:
              not "are we short" but "when did we need to have ordered".
            </p>
            <p className="text-on-surface font-medium">Safety stock</p>
            <p>
              The buffer you want to keep on hand to absorb a late delivery or a busier
              week than expected. Together with lead time it is what a reorder point is
              actually made of.
            </p>
            <p className="text-on-surface font-medium">Merging two items</p>
            <p>
              If the same physical part was entered twice under slightly different codes,
              an admin can merge one into the other. The stock is added together, the
              boxes and purchase-order lines come across, and the losing code is kept as a
              former name so labels already printed still scan. The losing row itself is
              deleted, which is the one part that cannot be undone.
            </p>
            <p className="text-on-surface font-medium">Renaming</p>
            <p>
              Admins can change the SKU. The old code is kept as a former name and still
              scans, so labels already printed keep working. Former names are listed under
              the title once there are any.
            </p>
            <p className="text-on-surface font-medium">Retiring</p>
            <p>
              A part we have stopped buying, or a prototype that did not work out, is
              retired rather than deleted. It disappears from every picker and every
              search, so nobody picks it for new work by hand, while the stock, the boxes
              and the orders it already has stay exactly as they are. A document that names
              its code or part number (a quote, a scanned order) still matches it, because
              the record is still real. Restoring it is one press on this page.
            </p>
          </HelpDrawer>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setRenaming(true)}
              className={buttonClass('quiet', 'sm')}
            >
              Rename
            </button>
          )}
          {/* Beside Rename because they share the alias mechanism, and separate
              from it because one changes a name and the other ends another item.
              Quieter than Rename by one shade: merging is rarer, and the button
              somebody reaches for by accident should not be the destructive one. */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setMerging(true)}
              className={buttonClass('quiet', 'sm')}
            >
              Merge in
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setChangingKind(true)}
              className={buttonClass('quiet', 'sm')}
            >
              Change kind
            </button>
          )}
          {/* The third door in the row, and the only one that changes what the
              item IS rather than what it is called. A pill rather than a bare
              word for exactly that reason: Rename and Merge edit names, this
              takes the item out of circulation, and the shape says so before
              the label does. Gated like the PATCH behind it (procurement or
              admin), not like Rename and Merge: retiring is reversible and
              deletes nothing, so it belongs to whoever edits the item. */}
          {canEdit && (
            <button
              type="button"
              onClick={() => setActive(!row.is_active)}
              disabled={retiring}
              className={buttonClass('quiet', 'sm')}
            >
              {row.is_active ? 'Retire item' : 'Restore item'}
            </button>
          )}
        </div>
        {/* The description is a SUBTITLE now, and only when it says something
            the name and the code do not. On most rows it was the SKU or the
            name written again. */}
        {row.description &&
          row.description !== itemLabel(row) &&
          row.description !== row.base_name &&
          row.description !== row.sku && (
            <p className="text-sm text-on-surface-variant mt-1">{row.description}</p>
          )}
        {/* Former names. Only rendered once there ARE any, so the common item
            carries no extra chrome. The point is that somebody holding a box
            with an old label can confirm they are in the right place. */}
        <Reveal show={skuHistory.length > 0}>
          <p className="text-xs text-on-surface-variant mt-1.5">
            Also known as{' '}
            {skuHistory.map((h, i) => (
              <React.Fragment key={h.sku}>
                {i > 0 && ', '}
                <Tooltip
                  label={
                    h.restored_at
                      ? 'Used again later'
                      : `Retired ${formatEventDate(h.retired_at, 'MMM D, YYYY')}${
                          h.reason ? `: ${h.reason}` : ''
                        }`
                  }
                >
                  <span className="font-mono text-on-surface-variant">{h.sku}</span>
                </Tooltip>
              </React.Fragment>
            ))}
            . These still scan.
          </p>
        </Reveal>
      </div>
        </>
      }
    >
      {/* Metadata card */}
      <Panel
        title="Metadata"
        actions={
          canEdit && !editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className={buttonClass('quiet', 'sm')}
            >
              Edit
            </button>
          ) : undefined
        }
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {/* ── IDENTITY ──────────────────────────────────────────────────
              What this item is called, and the five facts the generated name
              is built from. Everything below this is classification, planning
              or stock. */}
          <Field
            label="Name"
            value={
              editing ? (
                <TextField
                  size="sm"
                  maxLength={NAME_MAX}
                  value={(get('name') as string | null) || ''}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value || null })}
                  placeholder="What do people call this?"
                />
              ) : (
                // The STORED name, not the rendered one: this field is what a
                // person typed, and showing the generated fallback here would
                // make an empty field look filled in.
                row.name || <span className="text-on-surface-variant">generated from the fields below</span>
              )
            }
          />
          <Field
            label="Kind"
            value={
              editing ? (
                <Select
                  size="sm"
                  label="Kind"
                  placeholder="(none)"
                  value={(get('kind_key') as string | null) || ''}
                  onChange={(key) => setDraft({ ...draft, kind_key: key || null })}
                  options={[
                    { key: '', label: '(none)' },
                    ...(() => {
                      const current = (get('kind_key') as string | null) || '';
                      return current && !kindKnown(current)
                        ? [{ key: current, label: `${current} (retired)` }]
                        : [];
                    })(),
                    ...boardKinds.map((k) => ({ key: k.key, label: k.label || k.key, group: 'Board codes' })),
                    ...stemKinds.map((k) => ({ key: k.key, label: k.label || k.key, group: 'Short codes' })),
                  ]}
                  // Same in-place create the SKU builder offers: nobody
                  // leaves a half-edited item to visit an admin page.
                  actions={[{ key: NEW_KIND, label: 'New type…', onSelect: () => setAddingKind(true) }]}
                />
              ) : (
                (row.kind_key && kindLabel(kinds, row.kind_key)) || '—'
              )
            }
          />
          <Field
            label="Drone generation"
            value={
              editing ? (
                <>
                  <TextField
                    size="sm"
                    type="number"
                    min={0}
                    max={99}
                    aria-label="Drone generation"
                    value={(get('platform_generation') as number | null) ?? ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        platform_generation: e.target.value ? parseInt(e.target.value, 10) : null,
                      })
                    }
                  />
                  <p className="text-[11px] text-on-surface-variant mt-1">
                    Which drone generation this part is for
                  </p>
                </>
              ) : (
                <span className="tabular-nums">{row.platform_generation ?? '—'}</span>
              )
            }
          />
          <Field
            label="Revision"
            value={
              editing ? (
                <TextField
                  size="sm"
                  aria-label="Revision"
                  value={(get('revision') as string | null) || ''}
                  onChange={(e) => setDraft({ ...draft, revision: e.target.value || null })}
                  placeholder="This part's own iteration"
                />
              ) : (
                row.revision || '—'
              )
            }
          />
          <Field
            label="Variant"
            value={
              editing ? (
                <TextField
                  size="sm"
                  aria-label="Variant"
                  value={(get('variant') as string | null) || ''}
                  onChange={(e) => setDraft({ ...draft, variant: e.target.value || null })}
                />
              ) : (
                row.variant || '—'
              )
            }
          />
          <Field
            label="PCBA vendor"
            value={
              editing ? (
                <Select
                  size="sm"
                  label="PCBA vendor"
                  placeholder="(none)"
                  value={(get('pcba_vendor_id') as string | null) || ''}
                  onChange={(key) => setDraft({ ...draft, pcba_vendor_id: key || null })}
                  options={[
                    { key: '', label: '(none)' },
                    ...(() => {
                      // The stored vendor when the list did not load at all.
                      // Better a placeholder than a select that silently reads
                      // "(none)" over a real answer.
                      const current = (get('pcba_vendor_id') as string | null) || '';
                      return current && !pcbaVendors.some((v) => v.id === current)
                        ? [{ key: current, label: 'Current vendor' }]
                        : [];
                    })(),
                    // A retired vendor is offered ONLY where it is already the
                    // answer, so an old board keeps its assembler through an
                    // unrelated edit and no new board can be sent to a shop we
                    // have stopped using.
                    //
                    // Anchored on the SAVED value, not the draft. Reading the
                    // draft meant the allowance moved with each pick: choose a
                    // live vendor once and the retired one it replaced dropped
                    // out of the list, so changing your mind inside a single
                    // edit was a one-way door. What the row still says is the
                    // fact that earns the exception; the draft is only what
                    // somebody is trying next.
                    ...pcbaVendors
                      .filter((v) => !v.is_disabled || v.id === (row.pcba_vendor_id || ''))
                      .map((v) => ({ key: v.id, label: vendorOptionLabel(v) })),
                  ]}
                  // Same in-place create the Kind picker above offers: the
                  // assembler of a board we have just cataloged may not be
                  // in the vendor list yet, and walking to the admin page
                  // means losing a half-edited row.
                  actions={[{ key: NEW_VENDOR, label: 'New vendor…', onSelect: () => setAddingVendor(true) }]}
                />
              ) : (
                (() => {
                  const v = pcbaVendors.find((x) => x.id === row.pcba_vendor_id);
                  if (v) return vendorLabel(v);
                  return row.pcba_vendor_id ? 'Unknown vendor' : '—';
                })()
              )
            }
          />
          <Field
            label="Prototype"
            value={
              editing ? (
                <Checkbox
                  aria-label="Prototype"
                  checked={!!get('is_prototype')}
                  onChange={(e) => setDraft({ ...draft, is_prototype: e.target.checked })}
                />
              ) : row.is_prototype ? (
                'Yes'
              ) : (
                'No'
              )
            }
          />
          {/* Manufacturer immediately before MPN, because they are one fact in
              two halves and an MPN alone is ambiguous across distributors.
              Editable here rather than mint-only: most items get this filled in
              later, by whoever next has the datasheet or a quote open. */}
          <Field
            label="Manufacturer"
            value={
              editing ? (
                <TextField
                  size="sm"
                  value={(get('manufacturer') as string | null) || ''}
                  onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value || null })}
                  placeholder="Who makes it, not who sells it"
                />
              ) : (
                row.manufacturer || '—'
              )
            }
          />
          <Field
            label="MPN"
            value={
              editing ? (
                <TextField
                  size="sm"
                  value={(get('mpn') as string | null) || ''}
                  onChange={(e) => setDraft({ ...draft, mpn: e.target.value || null })}
                />
              ) : (
                row.mpn || '—'
              )
            }
          />
          <Field
            label="Family"
            value={
              <PartFamilyField
                partId={row.id}
                familyId={row.part_family_id ?? null}
                familyLabel={row.family_label}
                families={families}
                onChanged={(next) => setRow((r) => (r ? { ...r, ...next } : r))}
                onFamiliesChanged={refreshFamilies}
              />
            }
          />
          {/* Both classification axes. They have been PATCHable since 016 with
              no control anywhere to set them — this and the catalog's own
              columns are the editors that comment said were owed. The guide
              button sits with them for the same reason it sits on every mint
              surface: the question people stall on is at the boundaries. */}
          <Field
            label={
              <span className="inline-flex items-center gap-2">
                Build stage
                <ClassificationGuideButton label="?" />
              </span>
            }
            value={
              editing ? (
                <Select
                  size="sm"
                  label="Build stage"
                  value={(get('material_type') as MaterialType) || ''}
                  onChange={(key) => setDraft({ ...draft, material_type: key as MaterialType })}
                  options={MATERIAL_TYPES.map((t) => ({ key: t, label: MATERIAL_TYPE_META[t].label }))}
                />
              ) : (
                <MaterialTypeBadge materialType={row.material_type} />
              )
            }
          />
          <Field
            label="Item type"
            value={
              editing ? (
                <Select
                  size="sm"
                  label="Item type"
                  value={(get('commodity_class') as CommodityClass) || ''}
                  onChange={(key) => setDraft({ ...draft, commodity_class: key as CommodityClass })}
                  options={COMMODITY_CLASSES.map((c) => ({ key: c, label: COMMODITY_CLASS_META[c].label }))}
                />
              ) : (
                <CommodityClassBadge commodityClass={row.commodity_class} inline={false} />
              )
            }
          />
          <Field
            label="Consumable"
            value={
              editing ? (
                <Checkbox
                  aria-label="Consumable"
                  checked={get('is_consumable') as boolean}
                  onChange={(e) => setDraft({ ...draft, is_consumable: e.target.checked })}
                />
              ) : row.is_consumable ? (
                'Yes'
              ) : (
                'No'
              )
            }
          />
          <Field
            label="Per-instance tracked"
            value={
              editing && isAdmin ? (
                <Checkbox
                  aria-label="Per-instance tracked"
                  checked={get('tracked_per_instance') as boolean}
                  onChange={(e) =>
                    setDraft({ ...draft, tracked_per_instance: e.target.checked })
                  }
                />
              ) : row.tracked_per_instance ? (
                'Yes'
              ) : (
                'No'
              )
            }
          />
          <Field
            label="Reorder threshold"
            value={
              editing ? (
                <TextField
                  size="sm"
                  type="number"
                  min={0}
                  value={(get('reorder_threshold') as number | null) ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      reorder_threshold: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                />
              ) : (
                row.reorder_threshold ?? '—'
              )
            }
          />
          {/* Planning inputs (migration 033). NOT financial — build capacity is
              a manufacturing screen and "when does this arrive" is a
              manufacturing question. Money over the same projection is the
              can_financial view. */}
          <Field
            label="Lead time (days)"
            value={
              editing ? (
                <TextField
                  size="sm"
                  type="number"
                  min={0}
                  max={3650}
                  placeholder="Ask the vendor"
                  value={(get('lead_time_days') as number | null) ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      lead_time_days: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                />
              ) : (
                // Blank is not zero. Zero would mean "arrives the day you order".
                row.lead_time_days ?? <span className="text-on-surface-variant">not asked yet</span>
              )
            }
          />
          <Field
            label="Safety stock"
            value={
              editing ? (
                <TextField
                  size="sm"
                  type="number"
                  min={0}
                  value={(get('safety_stock') as number | null) ?? ''}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      safety_stock: e.target.value ? parseInt(e.target.value, 10) : null,
                    })
                  }
                />
              ) : (
                row.safety_stock ?? '—'
              )
            }
          />
          {canSeeFinancials && (
            <Field label="Weighted-avg unit cost" value={formatCurrency(row.unit_cost)} />
          )}
          <Field label="Total on-hand" value={<span className="tabular-nums">{totalOnHand}</span>} />
        </div>
        <Reveal show={editing}>
          <div className="mt-3 flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setDraft({});
              }}
              className={buttonClass('quiet', 'sm')}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className={buttonClass('filled', 'sm')}
            >
              Save
            </button>
          </div>
        </Reveal>
      </Panel>

      {/* Inventory by location. The vessel is the DataTable's — the page's own
          box, header band and row borders are gone, and the heading and the
          Adjust control ride the working strip where the section header used to
          hold them (the band is a flex row with min-w-0, so `ml-auto` parks the
          button exactly where justify-between had it).

          `replace` keeps today's behavior: the message stands IN PLACE OF the
          table rather than sitting under column names over nothing. */}
      <section aria-labelledby="mpn-inventory-heading">
        <DataTable<InventoryRollupRow>
          data={inventory}
          columns={INVENTORY_COLUMNS}
          getRowId={inventoryRowId}
          strip={
            <>
              <h2 id="mpn-inventory-heading" className="text-sm font-semibold text-on-surface">
                Inventory by Location
              </h2>
              {canAdjust || canSeeFinancials ? (
                <button
                  type="button"
                  onClick={() => setAdjustOpen(true)}
                  className={`${buttonClass('quiet', 'sm')} ml-auto`}
                >
                  Adjust
                </button>
              ) : null}
            </>
          }
          empty={<Empty label="No on-hand quantities for this SKU yet." />}
          emptyMode="replace"
        />
      </section>

      {/* Revision breakdown. Unsticky, like box detail's histories: this is one
          section of a document page, not the page's own list surface. */}
      <section aria-labelledby="mpn-revisions-heading">
        <DataTable<RevisionRow>
          data={revisionRows}
          columns={REVISION_COLUMNS}
          getRowId={revisionRowId}
          strip={
            <h2 id="mpn-revisions-heading" className="text-sm font-semibold text-on-surface">
              Revisions in stock ({recentBoxes.length} boxes, 90d)
            </h2>
          }
          empty={<Empty label="No boxes received in the last 90 days." />}
          emptyMode="replace"
        />
      </section>

      {/* No row is fine: a total at zero is not listed, and the drawer picks a place itself. */}
      {adjustOpen && (
        <InventoryAdjustAction
          partCatalogId={partCatalogId}
          sku={row.sku}
          item={row}
          locationId={inventory[0]?.location_id ?? null}
          locationName={inventory[0]?.location_name ?? null}
          unitCost={row.unit_cost}
          onClose={() => setAdjustOpen(false)}
          onAdjusted={() => {
            setAdjustOpen(false);
            fetchAll();
          }}
          onPriceChanged={() => fetchAll()}
        />
      )}

      {renaming && (
        <RenameSkuModal
          itemId={partCatalogId}
          currentSku={row.sku}
          onClose={() => setRenaming(false)}
          onRenamed={() => {
            setRenaming(false);
            // Full refetch rather than a local splice: the rename also moves
            // batch_assignments and adds a row to the history panel, so the
            // page has more than one thing to catch up on.
            fetchAll();
          }}
        />
      )}

      {changingKind && (
        <ChangeKindModal
          itemId={partCatalogId}
          currentSku={row.sku}
          currentKindKey={row.kind_key || null}
          mpn={row.mpn}
          onClose={() => setChangingKind(false)}
          onChanged={() => {
            setChangingKind(false);
            fetchAll();
          }}
        />
      )}

      {addingKind && (
        <ItemKindForm
          partNumberHint={row.mpn || null}
          onClose={() => setAddingKind(false)}
          onCreated={(kind) => {
            // Straight back into the field that asked for it. The store already
            // holds it, so the option exists by the time this renders.
            setDraft((d) => ({ ...d, kind_key: kind.key }));
            setAddingKind(false);
          }}
        />
      )}

      {/* Its own drawer rather than embedded, unlike the New Item wizard's
          copy: there is no modal around this page to nest inside, and an
          overlay is what the Kind form one field up already does. */}
      {addingVendor && (
        <VendorForm
          mode="create"
          types={vendorTypes}
          defaultType="pcba"
          onClose={() => setAddingVendor(false)}
          onSaved={(saved?: VendorFullRow) => {
            setAddingVendor(false);
            if (!saved) return;
            // Into the list and into the field that asked, without a refetch
            // that would also throw away the rest of the draft.
            setPcbaVendors((prev) =>
              prev.some((v) => v.id === saved.id)
                ? prev
                : [...prev, { id: saved.id, code: saved.code, name: saved.name, is_disabled: false }]
            );
            setDraft((d) => ({ ...d, pcba_vendor_id: saved.id }));
          }}
        />
      )}

      {merging && (
        <MergeItemModal
          itemId={partCatalogId}
          currentSku={row.sku}
          currentItem={row}
          onClose={() => setMerging(false)}
          onMerged={() => {
            setMerging(false);
            // Full refetch, and more so than the rename: a merge changes the
            // stock, the box list, the transaction list, the former-names panel
            // and the unit cost, all at once.
            fetchAll();
          }}
        />
      )}
    </PageLayout>
  );
};

// One fact, on its own tile. The tile is what makes a two-column grid of
// twenty facts scannable: without it the labels and values interleave into a
// wall and the eye has nothing to land on. A step above the section shell, so
// the shell still reads as the thing holding them.
const Field: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg bg-surface-container px-3 py-2">
    <div className="text-[11px] uppercase tracking-wider text-on-surface-variant mb-0.5">{label}</div>
    <div className="text-sm text-on-surface">{value}</div>
  </div>
);

// Both sub-tables' empty message. Nothing but a self-padded, self-colored node:
// the DataTable renders it in the field IN PLACE OF the table, so this is the
// whole of what an empty section says.
const Empty: React.FC<{ label: string }> = ({ label }) => (
  <EmptyState variant="area">{label}</EmptyState>
);

export default PartMPNDetail;
